/** @typedef {{ sanskrit: string, russian: string, english: string, category: string, roots: { root: string, meaning: string }[], shape: string, imageUrl?: string }} Pose */

/** Fisher–Yates shuffle (копия массива) */
export function shufflePoses(/** @type {Pose[]} */ list) {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Familiarity weight for a pose from its quiz progress: unseen/mistaken poses
 * score higher (more likely to be picked); poses answered correctly many
 * times score lower but never hit zero, so review keeps happening occasionally.
 * @param {{ correct?: number, incorrect?: number }} [progress]
 */
export function poseWeight(progress) {
  if (!progress) return 3;
  const { correct = 0, incorrect = 0 } = progress;
  const familiarity = correct - incorrect;
  return Math.max(0.3, 3 - familiarity);
}

/**
 * Weighted shuffle: samples poses without replacement using their familiarity
 * weight, so unfamiliar/mistaken poses tend to land earlier in the deck, while
 * still being freshly randomized (not a stable sort) on every call.
 * @param {Pose[]} list
 * @param {Record<string, { correct?: number, incorrect?: number }>} [progressBySanskrit]
 */
export function weightedShufflePoses(list, progressBySanskrit = {}) {
  const pool = list.map((pose) => ({ pose, weight: poseWeight(progressBySanskrit[pose.sanskrit]) }));
  const result = [];
  while (pool.length) {
    const total = pool.reduce((sum, item) => sum + item.weight, 0);
    let r = Math.random() * total;
    let idx = pool.length - 1;
    for (let i = 0; i < pool.length; i++) {
      r -= pool[i].weight;
      if (r <= 0) {
        idx = i;
        break;
      }
    }
    result.push(pool.splice(idx, 1)[0].pose);
  }
  return result;
}

/** Weighted random pick of a single pose (with replacement), favoring unfamiliar/mistaken ones. */
export function weightedPickPose(list, progressBySanskrit = {}) {
  const weights = list.map((pose) => poseWeight(progressBySanskrit[pose.sanskrit]));
  const total = weights.reduce((sum, w) => sum + w, 0);
  let r = Math.random() * total;
  for (let i = 0; i < list.length; i++) {
    r -= weights[i];
    if (r <= 0) return list[i];
  }
  return list[list.length - 1];
}

/**
 * A pose is "learned" once it's answered correctly enough, net of mistakes, to hit
 * the same familiarity floor that makes the quiz stop prioritizing it (poseWeight's floor).
 */
export function isPoseLearned(progress) {
  if (!progress) return false;
  return progress.correct - progress.incorrect >= 3;
}

/**
 * @param {Pose} pose
 * @param {string} q
 */
export function poseMatchesSearch(pose, q) {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return (
    pose.sanskrit.toLowerCase().includes(s) ||
    pose.russian.toLowerCase().includes(s) ||
    pose.english.toLowerCase().includes(s)
  );
}

export const POSE_CATEGORIES = [
  "стоя",
  "сидя",
  "прогиб",
  "баланс",
  "силовые",
  "перевёрнутые",
  "расслабление",
];
